import React, { useState, useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import ItemService from "../../../../services/customer_appCommonService";
import ClosureItemActionModal from "./ClosureItemActionModal";

// ERF type codes that involve physical equipment going to the customer
// and therefore need a Return Process flow when the rental closes.
// SL (Sale-only) is excluded — there's nothing to collect back.
export const RENTABLE_ERF_TYPES = ["RE", "SLRE"];

export const isRentableErfType = (erfTypeCode) =>
  RENTABLE_ERF_TYPES.includes(erfTypeCode);

const formatIST = (isoString) => {
  if (!isoString) return { date: "--", time: "" };
  const d = new Date(isoString);
  const date = d.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return { date, time };
};

// "Receive Product" is the only relevant category for the closure return flow.
// The full list is kept here in case the dropdown needs to be shown elsewhere.
export const serviceAssignentCategoryOption = [
  { id: 1, name: "Dispatch", description: "Dispatch", code: "Dispatch" },
  {
    id: 2,
    name: "Replacement",
    description: "Replacement",
    code: "Replacement",
  },
  { id: 3, name: "Service", description: "Service", code: "Service" },
  {
    id: 4,
    name: "Collect Money",
    description: "Collect Money",
    code: "Collect Money",
  },
  {
    id: 5,
    name: "Receive Product",
    description: "Receive Product",
    code: "Receive Product",
  },
];

// Default assignment category for the closure-return (product collection) flow
const CLOSURE_ASSIGN_CATEGORY = serviceAssignentCategoryOption.find(
  (o) => o.name === "Receive Product",
);

// ── Status badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Pending: { bg: "#ffc107", color: "black" },
    Collected: { bg: "#0d6efd", color: "white" },
    Done: { bg: "#198754", color: "white" },
  };
  const s = map[status] || map.Pending;
  return (
    <span
      className="badge"
      style={{ ...s, padding: "5px 10px", borderRadius: "8px" }}
    >
      {status || "Pending"}
    </span>
  );
};

// Build a lookup map: (barcode || materialCode+equipmentItemNr) → productReturned flag
// Used to sync productReturned from the live rentItems/nursingItems into closure rows.
// const buildReturnedLookup = (erfData) => {
//   const map = new Map();
//   [...(erfData?.rentItems || []), ...(erfData?.nursingItems || [])].forEach(
//     (item) => {
//       if (item.isTotal) return;
//       const key = item.barcode
//         ? item.barcode
//         : `${item.materialCode}__${item.equipmentItemNr || ""}`;
//       map.set(key, !!item.productReturned);
//     },
//   );
//   return map;
// };

// const itemKey = (item) =>
//   item.barcode
//     ? item.barcode
//     : `${item.materialCode}__${item.equipmentItemNr || ""}`;

const itemKey = (item) => {
  const prefix = item.isAccessory ? "ACC" : "ITM";
  if (item.barcode) return `${prefix}__${item.barcode}`;
  return `${prefix}__${item.materialCode}__${item.equipmentItemNr || ""}__${item.parentLocalId || ""}`;
};

// const buildInitialItemsFromRentItems = (erfData) => {
//   // Only include items that are Closed AND not yet returned (productReturned !== true)
//   const closedRent = (erfData?.rentItems || []).filter(
//     (item) =>
//       !item.isTotal &&
//       item.extensionStatus === "Closed" &&
//       !item.productReturned,
//   );

//   const closedNursing = (erfData?.nursingItems || []).filter(
//     (item) =>
//       !item.isTotal &&
//       item.extensionStatus === "Closed" &&
//       !item.productReturned,
//   );

//   // Include accessories whose parent is in the closed set
//   const closedParentLocalIds = new Set([
//     ...closedRent.map((i) => i.local_id),
//     ...closedNursing.map((i) => i.local_id),
//   ]);
//   const closedAccessories = (erfData?.accessoryItems || []).filter(
//     (item) =>
//       item.extensionStatus === "Closed" &&
//       !item.productReturned &&
//       closedParentLocalIds.has(item.parentLocalId),
//   );

//   const sourceRows = [
//     ...closedRent.map((item) => ({ ...item, isAccessory: false })),
//     ...closedNursing.map((item) => ({ ...item, isAccessory: false })),
//     ...closedAccessories.map((item) => ({ ...item, isAccessory: true })),
//   ];

//   return sourceRows.map((item) => ({
//     barcode: item.barcode || "",
//     materialCode: item.materialCode || "",
//     materialDescription: item.materialDescription || "",
//     itemType: item.itemType || "Rent",
//     orderedQuantity: item.orderedQuantity || 1,
//     documentNumber: erfData.documentNumber || "",
//     equipmentItemNr: item.equipmentItemNr || "",
//     equipmentRentNr: item.equipmentRentNr || item.documentNumber || "",
//     erfTypeCode: item.erfTypeCode || "RE",
//     isAccessory: !!item.isAccessory,
//     parentLocalId: item.parentLocalId || "",
//     parentMaterialCode: item.parentMaterialCode || "",
//     parentDescription: item.parentDescription || "",
//     collectionStatus: "Pending",
//     collectedBy: "",
//     collectedAt: "",
//     collectionRemarks: "",
//     condition: "",
//     stockInStatus: "Pending",
//     stockInBy: "",
//     stockInAt: "",
//     stockInRemarks: "",
//     qualityCheck: null,
//     productReturned: false,
//   }));
// };
// ─────────────────────────────────────────────────────────────────────────────
// Builds the canonical list of closure rows from the ERF document.
//
// Rules:
//   1. A rent or nursing item is included if extensionStatus === "Closed".
//   2. An accessory is included if its parent (rent or nursing) is included
//      AND the accessory itself is "Closed". Both checks together prevent
//      orphan accessories from appearing.
//   3. productReturned is preserved from the source — that's the canonical
//      "this item has been collected & stocked in" flag.
// ─────────────────────────────────────────────────────────────────────────────
const buildClosureRowsFromErf = (erfData) => {
  const isClosedSourceRow = (row) =>
    !row?.isTotal && row?.extensionStatus === "Closed";

  // Step 1 — closed rent + nursing items (the "parents")
  const closedRentParents = (erfData?.rentItems || []).filter(
    isClosedSourceRow,
  );
  const closedNursingParents = (erfData?.nursingItems || []).filter(
    isClosedSourceRow,
  );

  // Step 2 — collect parent local_ids so accessories can be matched to them
  const closedParentLocalIds = new Set(
    [...closedRentParents, ...closedNursingParents].map((p) => p.local_id),
  );

  // Step 3 — accessories whose parent is closed AND that are themselves closed
  const closedAccessories = (erfData?.accessoryItems || []).filter(
    (acc) =>
      acc?.extensionStatus === "Closed" &&
      closedParentLocalIds.has(acc.parentLocalId),
  );

  // Step 4 — flat list, with isAccessory flag so the UI/key knows the type
  const allClosedSourceRows = [
    ...closedRentParents.map((row) => ({ ...row, isAccessory: false })),
    ...closedNursingParents.map((row) => ({ ...row, isAccessory: false })),
    ...closedAccessories.map((row) => ({ ...row, isAccessory: true })),
  ];

  return allClosedSourceRows.map(toClosureRow(erfData));
};

// Shape one source row into the closure-table row contract.
const toClosureRow = (erfData) => (sourceRow) => ({
  // ── Identity (drives itemKey, hydration, and stock-in patching) ───────────
  barcode: sourceRow.barcode || "",
  materialCode: sourceRow.materialCode || "",
  materialDescription: sourceRow.materialDescription || "",
  equipmentItemNr: sourceRow.equipmentItemNr || "",
  equipmentRentNr: sourceRow.equipmentRentNr || sourceRow.documentNumber || "",
  documentNumber: erfData.documentNumber || "",
  erfTypeCode: sourceRow.erfTypeCode || "RE",
  itemType: sourceRow.itemType || "Rent",
  orderedQuantity: sourceRow.orderedQuantity || 1,

  // ── Accessory linkage (so the UI can group / itemKey can disambiguate) ────
  isAccessory: !!sourceRow.isAccessory,
  parentLocalId: sourceRow.parentLocalId || "",
  parentMaterialCode: sourceRow.parentMaterialCode || "",
  parentDescription: sourceRow.parentDescription || "",

  // ── Per-row workflow state (defaults; later overwritten by hydration) ─────
  collectionStatus: "Pending",
  collectedBy: "",
  collectedAt: "",
  collectionRemarks: "",
  condition: "",
  stockInStatus: "Pending",
  stockInBy: "",
  stockInAt: "",
  stockInRemarks: "",
  qualityCheck: null,

  // ── Canonical "is this row already returned?" flag from the source ────────
  productReturned: !!sourceRow.productReturned,
});

const ClosureReturnSection = ({
  erfData,
  documentNumber,
  isExtension = false,
  onRefresh,
}) => {
  // ── State ─────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [closureReturn, setClosureReturn] = useState(null);
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [serviceExecutiveList, setServiceExecutiveList] = useState([]);
  const [selectedSEUserId, setSelectedSEUserId] = useState("");

  // Modal: { mode: "collect"|"stockin", itemIndex: number }
  const [modal, setModal] = useState(null);

  // Accordion toggles
  const [showAssign, setShowAssign] = useState(true);
  const [showItems, setShowItems] = useState(true);
  const [showHistory, setShowHistory] = useState(true);

  // ── Role detection ────────────────────────────────────────────────────────
  const userData = useSelector((state) => state?.user);
  const currentUserId = userData?.data?.userId || "";
  const currentName = userData?.data?.firstName || userData?.data?.userId || "";

  const isDispatch = useMemo(
    () => userData?.data?.roles?.some((r) => r.roleId === "DISPATCH") ?? false,
    [userData],
  );
  const isServiceExecutive = useMemo(
    () =>
      userData?.data?.roles?.some((r) =>
        ["SERVICE-EXECUTIVE", "SE-EXECUTIVE", "SEREX", "SALES-SE"].includes(
          r.roleId,
        ),
      ) ?? false,
    [userData],
  );
  const isStore = useMemo(
    () => userData?.data?.roles?.some((r) => r.roleId === "STORE") ?? false,
    [userData],
  );

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (erfData) initClosureData();
    // eslint-disable-next-line
  }, [erfData]);

  useEffect(() => {
    fetchServiceExecutiveList();
  }, []);

  // ── Fetch SE list ─────────────────────────────────────────────────────────
  const fetchServiceExecutiveList = async () => {
    try {
      const obj = {
        queryName: "get_ServiceProvider_list",
        page: 1,
        per_page: 999,
        timeZone: "Asia/Calcutta",
      };
      const res = await ItemService.generateQuery(obj);
      if (res?.status === 200 && Array.isArray(res.data)) {
        setServiceExecutiveList(
          res.data.map((s) => ({
            userId: s.userId,
            name: s.firstName || s.name,
            code: s.userId,
          })),
        );
      }
    } catch (err) {
      console.error("SE list fetch error", err);
    }
  };

  // ── Initialise closure data from API response ─────────────────────────────
  // const initClosureData = () => {
  //   const existing = erfData?.closureReturn;

  //   // Build a lookup so we can sync productReturned from live rentItems/nursingItems
  //   // into closure rows — ensures stock-in flag is always current even if closure
  //   // items were saved before the flag was written.
  //   const returnedLookup = buildReturnedLookup(erfData);

  //   if (existing?.items?.length > 0) {
  //     // PATH 1 -- closureReturn already saved on the record.
  //     // Re-merge productReturned from the live parent items so that items
  //     // stocked-in after the last save are correctly marked.
  //     const syncedItems = existing.items.map((item) => {
  //       const key = itemKey(item);
  //       const isReturned = returnedLookup.has(key)
  //         ? returnedLookup.get(key)
  //         : !!item.productReturned;
  //       return { ...item, productReturned: isReturned };
  //     });
  //     setClosureReturn(existing);
  //     setItems(syncedItems);
  //     setHistory(existing.closureHistory || []);
  //     setSelectedSEUserId(existing.seAssignment?.userId || "");
  //   } else {
  //     // PATH 2 -- fallback: build from closed rentItems (already filters productReturned)
  //     const fallback = buildInitialItemsFromRentItems(erfData);
  //     setItems(fallback);
  //     setHistory([]);
  //     setClosureReturn(null);
  //   }
  // };
  const initClosureData = () => {
    // Defensive: erfData might still be loading on first render.
    if (!erfData) {
      setItems([]);
      setHistory([]);
      setClosureReturn(null);
      setSelectedSEUserId("");
      return;
    }

    const existing = erfData?.closureReturn;

    // Source of truth: every Closed item from rentItems / nursingItems
    // (+ their closed accessories).
    const allClosedRows = buildClosureRowsFromErf(erfData);

    // Hydrate per-row collect / stock-in state from closureReturn.items.
    // productReturned is NOT hydrated — it stays canonical from the source row.
    const priorByKey = new Map();
    (existing?.items || []).forEach((it) => priorByKey.set(itemKey(it), it));

    const hydrated = allClosedRows.map((row) => {
      const prior = priorByKey.get(itemKey(row));
      if (!prior) return row;
      return {
        ...row,
        collectionStatus: prior.collectionStatus || "Pending",
        collectedBy: prior.collectedBy || "",
        collectedAt: prior.collectedAt || "",
        collectionRemarks: prior.collectionRemarks || "",
        condition: prior.condition || row.condition || "",
        stockInStatus: prior.stockInStatus || "Pending",
        stockInBy: prior.stockInBy || "",
        stockInAt: prior.stockInAt || "",
        stockInRemarks: prior.stockInRemarks || "",
        qualityCheck: prior.qualityCheck ?? null,
      };
    });

    setClosureReturn(existing || null);
    setItems(hydrated);
    setHistory(existing?.closureHistory || []);
    setSelectedSEUserId(existing?.seAssignment?.userId || "");
  };

  // ── Core save helper ──────────────────────────────────────────────────────
  // Saves ONLY the closureReturn sub-document (items + history + documents).
  // Header-level SE fields are handled separately in handleAssignSE.
  // const saveClosureReturn = async (
  //   updatedItems,
  //   updatedHistory,
  //   updatedAssignment,
  // ) => {
  //   const payload = {
  //     documentNumber,
  //     equipmentRentNr: documentNumber,
  //     equipmentNr: erfData.equipmentNr || documentNumber,
  //     closureReturn: {
  //       seAssignment: updatedAssignment || closureReturn?.seAssignment || null,
  //       items: updatedItems,
  //       closureHistory: updatedHistory,
  //     },
  //   };
  //   // console.log("PayloadForUpdateErfORExt", payload);

  //   const apiCall = isExtension
  //     ? ItemService.updateExtendEquipmentRent(payload)
  //     : ItemService.updateEquipmentRentForm(payload);

  //   await apiCall;
  // };
  const saveClosureReturn = async (
    updatedItems,
    updatedHistory,
    updatedAssignment,
  ) => {
    const payload = {
      documentNumber,
      equipmentRentNr: documentNumber,
      // equipmentNr: erfData.equipmentNr || documentNumber,
      closureReturn: {
        seAssignment: updatedAssignment || closureReturn?.seAssignment || null,
        items: updatedItems,
        closureHistory: updatedHistory,
      },
    };

    const apiCall = isExtension
      ? ItemService.updateExtendEquipmentRent(payload)
      : ItemService.updateEquipmentRentForm(payload);

    await apiCall;
  };

  // ── 1. Assign / Reassign SE ───────────────────────────────────────────────
  // This writes BOTH the closureReturn.seAssignment AND the header-level
  // assignedServiceExecutives / serviceProviderXxx fields so every part of
  // the system knows who is responsible for collecting this ERF.
  const handleAssignSE = async () => {
    if (!selectedSEUserId) {
      toast.error("Please select a Service Executive");
      return;
    }
    const serviceExecutiveInfo = serviceExecutiveList.find(
      (s) => s.userId === selectedSEUserId,
    );
    if (!serviceExecutiveInfo) return;

    setLoading(true);
    try {
      const now = new Date().toISOString();
      const assignment = {
        userId: serviceExecutiveInfo.userId,
        name: serviceExecutiveInfo.name,
        assignedBy: currentName,
        assignedAt: now,
      };

      const historyEntry = {
        action: "SE_ASSIGNED",
        performedBy: currentName,
        performedAt: now,
        details: { seAssigned: serviceExecutiveInfo.name },
      };
      const updatedHistory = [...history, historyEntry];

      // Build the new assignedServiceExecutive entry
      const newAssignment = {
        serviceProviderCode:
          serviceExecutiveInfo.code || serviceExecutiveInfo.userId,
        serviceProviderUserId: serviceExecutiveInfo.userId,
        serviceProviderName: serviceExecutiveInfo.name,
        serviceAssignCategory: CLOSURE_ASSIGN_CATEGORY.name,
        serviceAssignCategoryCode: CLOSURE_ASSIGN_CATEGORY.code,
        serviceAssignCategoryDescription: CLOSURE_ASSIGN_CATEGORY.description,
        assignDateAndTime: now,
        assignedBy: currentUserId,
        shouldCollectMoney: false, // product return -- no money
      };

      // Append to any existing entries so history of all assignments is kept
      const existingAssignedSEs = erfData?.assignedServiceExecutives || [];
      const updatedAssignedSEs = [...existingAssignedSEs, newAssignment];

      const hasCollectionProgress = items.some(
        (i) => i.collectionStatus === "Collected" || i.productReturned === true,
      );

      // ── Payload that updates BOTH header fields AND closureReturn ─────────
      const fullPayload = {
        documentNumber,
        equipmentRentNr: documentNumber,
        // equipmentNr: erfData.equipmentNr || documentNumber,

        // ── Header-level SE identity fields (Bug fix from requirements) ──────
        serviceAssignCategory: CLOSURE_ASSIGN_CATEGORY.name,
        serviceAssignCategoryCode: CLOSURE_ASSIGN_CATEGORY.code,
        serviceAssignCategoryDesc: CLOSURE_ASSIGN_CATEGORY.description,
        serviceAssignCategoryDescription: CLOSURE_ASSIGN_CATEGORY.description,
        serviceProviderCode:
          serviceExecutiveInfo.code || serviceExecutiveInfo.userId,
        serviceProviderName: serviceExecutiveInfo.name,
        serviceProviderUserId: serviceExecutiveInfo.userId,
        assignedServiceExecutives: updatedAssignedSEs,

        // ── closureReturn sub-document ────────────────────────────────────────
        closureReturn: {
          seAssignment: assignment,
          // items,
          items: hasCollectionProgress ? items : [],
          closureHistory: updatedHistory,
        },
      };

      // console.log("PayloadForUpdateErfORExt", fullPayload);

      const apiCall = isExtension
        ? ItemService.updateExtendEquipmentRent(fullPayload)
        : ItemService.updateEquipmentRentForm(fullPayload);
      await apiCall;

      // Update local state
      setClosureReturn((prev) => ({ ...prev, seAssignment: assignment }));
      setHistory(updatedHistory);
      toast.success(
        `${serviceExecutiveInfo.name} assigned for product collection`,
      );
    } catch (err) {
      console.error("SE assign error:", err);
      toast.error("Failed to assign Service Executive");
    } finally {
      setLoading(false);
    }
  };

  // ── 2. SE confirms collection ─────────────────────────────────────────────
  const handleCollectConfirm = async ({ remarks, condition }) => {
    const { itemIndex } = modal;
    setModal(null);
    setLoading(true);

    try {
      const updatedItems = items.map((item, idx) =>
        idx !== itemIndex
          ? item
          : {
              ...item,
              collectionStatus: "Collected",
              collectedBy: currentName,
              collectedAt: new Date().toISOString(),
              collectionRemarks: remarks,
              condition,
            },
      );

      const historyEntry = {
        action: "ITEM_COLLECTED",
        performedBy: currentName,
        performedAt: new Date().toISOString(),
        details: {
          barcode: items[itemIndex].barcode,
          material: items[itemIndex].materialDescription,
          condition,
          remarks,
        },
      };
      const updatedHistory = [...history, historyEntry];

      await saveClosureReturn(
        updatedItems,
        updatedHistory,
        closureReturn?.seAssignment,
      );
      setItems(updatedItems);
      setHistory(updatedHistory);
      toast.success("Item marked as collected");
      // Refresh full ERF data from DB so next stock-in reads current state
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error("Collect confirm error:", err);
      toast.error("Failed to update collection status");
    } finally {
      setLoading(false);
    }
  };

  // ── 3. Store does stock-in ────────────────────────────────────────────────
  const handleStockInConfirm = async ({ remarks, condition, qualityCheck }) => {
    const { itemIndex } = modal;
    setModal(null);
    setLoading(true);

    const item = items[itemIndex];

    try {
      const updatedItem = {
        ...item,
        stockInStatus: "Done",
        stockInBy: currentName,
        stockInAt: new Date().toISOString(),
        stockInRemarks: remarks,
        condition,
        qualityCheck,
        productReturned: true,
      };

      const updatedItems = items.map((r, i) =>
        i === itemIndex ? updatedItem : r,
      );

      const historyEntry = {
        action: "STOCK_IN",
        performedBy: currentName,
        performedAt: new Date().toISOString(),
        details: {
          barcode: item.barcode,
          material: item.materialDescription,
          condition,
          remarks,
          qualityCheck,
        },
      };
      const updatedHistory = [...history, historyEntry];

      // API 1 -- Update ERF closureReturn + patch productReturned: true on the
      // parent rentItem / nursingItem so refund columns get locked in ERFExtensionDetail.
      // The row the Store user just stocked in. We need to flip
      // productReturned: true on whichever source array this row lives in
      // (rent / nursing / accessory). The other two arrays pass through unchanged.
      const targetKey = itemKey(item);

      // Helper: stamp productReturned: true onto the matching source row.
      // Skips "total" summary rows. Uses itemKey, so accessory rows are matched
      // correctly because itemKey now distinguishes ACC vs ITM.
      const patchSourceRow = (sourceRow) => {
        if (sourceRow.isTotal) return sourceRow;
        return itemKey(sourceRow) === targetKey
          ? { ...sourceRow, productReturned: true }
          : sourceRow;
      };

      const patchRentItems = (erfData?.rentItems || []).map(patchSourceRow);
      const patchNursingItems = (erfData?.nursingItems || []).map(
        patchSourceRow,
      );

      // Accessories don't carry isAccessory on their backend rows, so we
      // inject it before keying — that way itemKey returns the ACC-prefixed
      // form and matches the closure row we just stocked in.
      const patchAccessoryItems = (erfData?.accessoryItems || []).map((acc) =>
        patchSourceRow({ ...acc, isAccessory: true }),
      );

      const closurePayload = {
        documentNumber,
        equipmentRentNr: documentNumber,
        // equipmentNr: erfData.equipmentNr || documentNumber,
        closureReturn: {
          seAssignment: closureReturn?.seAssignment || null,
          items: updatedItems,
          closureHistory: updatedHistory,
        },
        rentItems: patchRentItems,
        nursingItems: patchNursingItems,
        accessoryItems: patchAccessoryItems,
      };

      const apiCall = isExtension
        ? ItemService.updateExtendEquipmentRent(closurePayload)
        : ItemService.updateEquipmentRentForm(closurePayload);
      await apiCall;

      // const patchRentItems = (erfData?.rentItems || []).map((ri) => {
      //   if (ri.isTotal) return ri;
      //   const key = itemKey(item);
      //   if (itemKey(ri) === key) return { ...ri, productReturned: true };
      //   return ri;
      // });
      // const patchNursingItems = (erfData?.nursingItems || []).map((ni) => {
      //   if (ni.isTotal) return ni;
      //   const key = itemKey(item);
      //   if (itemKey(ni) === key) return { ...ni, productReturned: true };
      //   return ni;
      // });

      // const closurePayload = {
      //   documentNumber,
      //   equipmentRentNr: documentNumber,
      //   equipmentNr: erfData.equipmentNr || documentNumber,
      //   closureReturn: {
      //     seAssignment: closureReturn?.seAssignment || null,
      //     items: updatedItems,
      //     closureHistory: updatedHistory,
      //   },
      //   // Patch productReturned on the source items so ERFExtensionDetail locks refund
      //   rentItems: patchRentItems,
      //   nursingItems: patchNursingItems,
      // };

      // const apiCall = isExtension
      //   ? ItemService.updateExtendEquipmentRent(closurePayload)
      //   : ItemService.updateEquipmentRentForm(closurePayload);
      // await apiCall;

      // API 2 -- Update Material barcode record
      if (item.barcode) {
        await updateMaterialStockIn(item, condition);
      }

      setItems(updatedItems);
      setHistory(updatedHistory);
      toast.success("Stock in completed");
      // Refresh full ERF data from DB so next stock-in reads current state
      if (onRefresh) {
        await onRefresh();
      }
    } catch (err) {
      console.error("Stock in error:", err);
      toast.error(err.message || "Stock in failed -- please try again");
    } finally {
      setLoading(false);
    }
  };

  // ── Material barcode update on stock-in ───────────────────────────────────
  const fetchMaterialRecord = async (barcode) => {
    try {
      const obj = {
        queryName: "MaterilBarcodeList",
        rowFilter: { $and: [{ barcode: barcode }] },
        timeZone: "Asia/Calcutta",
      };
      const res = await ItemService.generateQuery(obj);
      return res?.status === 200 && res.data?.length > 0 ? res.data[0] : null;
    } catch (err) {
      console.error("Fetch material error", err);
      return null;
    }
  };

  const updateMaterialStockIn = async (item, condition) => {
    const materialRecord = await fetchMaterialRecord(item.barcode);
    if (!materialRecord) {
      throw new Error(
        `Could not fetch material record for barcode: ${item.barcode}`,
      );
    }

    const now = new Date().toISOString();

    const newHistoryEntry = {
      status: "Stock In",
      equipmentRentNr: item.documentNumber || "",
      equipmentItemNr: item.equipmentItemNr || "",
      erfTypeCode: item.erfTypeCode || "RE",
      condition,
      updatedAt: now,
      updatedBy: currentUserId,
      updatedByName: currentName,
    };

    const updatedBarcodeHistory = [
      ...(materialRecord.barcodeERFHistory || []),
      newHistoryEntry,
    ];

    const payload = {
      barcode: item.barcode,
      documentNumber: materialRecord.documentNumber,
      materialStatus: "StockIn",
      assignBarcode: false,
      barCode: "StockIn",
      barcodeERFHistory: updatedBarcodeHistory,
    };
    // console.log("payloadForBarcodeUpdate", payload);
    await ItemService.updateMaterialBarcodeItem(payload);
  };

  const onModalConfirm = (data) => {
    if (modal?.mode === "collect") handleCollectConfirm(data);
    else handleStockInConfirm(data);
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const itemColumns = [
    {
      name: "Material",
      width: "260px", // Slightly wider to help with wrapping
      cell: (row) => (
        <div className="py-2">
          {/* 1. Badge & Description */}
          <div className="text-sm font-bold leading-snug mb-1">
            <p>
              {row.isAccessory && (
                <span className="badge bg-secondary me-2">Accessory</span>
              )}
            </p>
            {/* If your data includes that stray "> ", you can chain .replace('> ', '') here */}
            {row.materialDescription}
          </div>

          {/* 2. Material Code */}
          <div className="text-xs text-gray-500 font-semibold mb-1">
            {row.materialCode}
          </div>

          {/* 3. Parent Linkage */}
          {row.isAccessory && row.parentDescription && (
            <div className="text-xs text-gray-400 pl-2 border-l-2 border-gray-300">
              ↳ For:
              <span className="text-[10px]"> {row.parentMaterialCode}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Barcode",
      width: "170px",
      cell: (row) => <span>{row.barcode}</span>,
    },
    {
      name: "Qty",
      width: "55px",
      cell: (row) => <span>{row.orderedQuantity}</span>,
    },
    {
      name: "Condition",
      width: "150px",
      cell: (row) => (
        <span
          style={{
            color: (row.condition || "").includes("Damage") ? "red" : "inherit",
          }}
        >
          {row.condition || "--"}
        </span>
      ),
    },
    {
      name: "Collection",
      width: "160px",
      cell: (row, rowIndex) => {
        const collected =
          row.collectionStatus === "Collected" || row.productReturned;
        return (
          <div className="py-1">
            <StatusBadge
              status={row.productReturned ? "Collected" : row.collectionStatus}
            />
            {collected && (
              <>
                <div className="text-muted mt-1">{row?.collectedBy || "—"}</div>
                <p>{row?.collectedAt ? formatIST(row.collectedAt).date : ""}</p>
              </>
            )}
            {/* {isServiceExecutive &&
              !collected &&
              closureReturn?.seAssignment && (
                <button
                  className="btn btn-sm btn-outline-primary mt-1"
                  onClick={() =>
                    setModal({ mode: "collect", itemIndex: rowIndex })
                  }
                >
                  Collect
                </button>
              )} */}
            {isServiceExecutive &&
              !collected &&
              closureReturn?.seAssignment && (
                <button
                  className="btn btn-sm btn-outline-primary mt-1"
                  onClick={() =>
                    setModal({ mode: "collect", itemIndex: rowIndex })
                  }
                  disabled={loading}
                  style={{
                    opacity: loading ? 0.5 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Processing..." : "Collect"}
                </button>
              )}
          </div>
        );
      },
    },
    {
      name: "Stock In",
      width: "160px",
      cell: (row, rowIndex) => {
        // Nursing items are a service — no physical stock in applies
        if (row.itemType === "Nursing") {
          return <span className="text-muted small">N/A</span>;
        }
        const collected =
          row.collectionStatus === "Collected" || row.productReturned;
        const done = row.stockInStatus === "Done" || row.productReturned;
        return (
          <div className="py-1">
            <StatusBadge status={done ? "Done" : row.stockInStatus} />
            {done && (row.stockInBy || row.stockInAt) && (
              <div className="text-muted mt-1">
                {row?.stockInBy}
                {row?.stockInAt ? ` · ${formatIST(row.stockInAt).date}` : ""}
              </div>
            )}
            {/* {isStore && collected && !done && (
              <button
                className="btn btn-sm btn-outline-success mt-1"
                onClick={() =>
                  setModal({ mode: "stockin", itemIndex: rowIndex })
                }
              >
                Stock In
              </button>
            )} */}
            {isStore && collected && !done && row.itemType !== "Nursing" && (
              <button
                className="btn btn-sm btn-outline-success mt-1"
                onClick={() =>
                  setModal({ mode: "stockin", itemIndex: rowIndex })
                }
                disabled={loading}
                style={{
                  opacity: loading ? 0.5 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Processing..." : "Stock In"}
              </button>
            )}
          </div>
        );
      },
    },

    {
      name: "SE Remarks",
      width: "160px",
      cell: (row) => <span>{row.collectionRemarks || "--"}</span>,
    },
    {
      name: "Store Remarks",
      width: "160px",
      cell: (row) => <span>{row.stockInRemarks || "--"}</span>,
    },
  ];

  const historyColumns = [
    {
      name: "Date / Time",
      width: "140px",
      cell: (row) => {
        const { date, time } = formatIST(row.performedAt);
        return (
          <div>
            <div className="fw-medium">{date}</div>
            <div className="text-muted">{time}</div>
          </div>
        );
      },
    },
    {
      name: "Action",
      width: "150px",
      cell: (row) => {
        const map = {
          SE_ASSIGNED: { label: "SE Assigned", bg: "#0d6efd" },
          ITEM_COLLECTED: { label: "Item Collected", bg: "#fd7e14" },
          STOCK_IN: { label: "Stock In Done", bg: "#198754" },
        };
        const s = map[row.action] || { label: row.action, bg: "#6c757d" };
        return (
          <span
            className="badge"
            style={{ backgroundColor: s.bg, color: "white" }}
          >
            {s.label}
          </span>
        );
      },
    },
    {
      name: "Performed By",
      width: "150px",
      cell: (row) => <span className="fw-medium">{row.performedBy}</span>,
    },
    {
      name: "Details",
      width: "350px",
      cell: (row) => (
        <div>
          {row.details?.seAssigned && (
            <p className="mb-1">
              <strong>SE:</strong> {row.details.seAssigned}
            </p>
          )}
          {row.details?.material && (
            <p className="mb-1">
              <strong>Item:</strong> {row.details.material}
            </p>
          )}
          {row.details?.condition && (
            <p className="mb-1">
              <strong>Condition:</strong> {row.details.condition}
            </p>
          )}
          {row.details?.remarks && (
            <p className="mb-0">
              <strong>Remarks:</strong> {row.details.remarks}
            </p>
          )}
        </div>
      ),
    },
  ];

  const totalItems = items.length;
  const collectedCount = items.filter(
    (i) => i.collectionStatus === "Collected",
  ).length;
  const stockedInCount = items.filter((i) => i.stockInStatus === "Done").length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="accordion mb-4" id="accordionClosureReturn">
      {!isRentableErfType(erfData?.erfTypeCode) ? (
        <div className="p-4 text-center text-muted">
          <i className="bi bi-info-circle me-2"></i>
          Return Process is not applicable for ERF type "
          {erfData?.erfType || erfData?.erfTypeCode || "Unknown"}". Only Rent,
          Sale &amp; Rent, and Nursing Rent ERFs require physical item return.
        </div>
      ) : (
        <div className="accordion-item">
          {/* ── Section Header ─────────────────────────────────────────────── */}
          <h2 className="accordion-header">
            <Row className="accordion-button">
              <Col xs={1} className="text-left arrowBtn">
                <i className="bi bi-arrow-return-left" />
              </Col>
              <Col xs={1} className="text-center logoBtn">
                <span className="iconLogo">CR</span>
              </Col>
              <Col xs={10}>
                <span className="headerTitle">Closure Return</span>
                <span className="subHeaderTitle">
                  {collectedCount}/{totalItems} collected &nbsp;·&nbsp;
                  {stockedInCount}/{totalItems} stocked in
                </span>
              </Col>
            </Row>
          </h2>

          <div className="accordion-collapse collapse show visible">
            <div className="accordion-body">
              {/* ── 1. SE Assignment ───────────────────────────────────────── */}
              <div className="accordion mb-3">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${showAssign ? "bi-chevron-down" : "bi-chevron-right"}`}
                          onClick={() => setShowAssign(!showAssign)}
                        />
                      </Col>
                      <Col xs={1} className="text-center logoBtn">
                        <span className="iconLogo">SA</span>
                      </Col>
                      <Col xs={10}>
                        <span className="headerTitle">
                          Service Executive Assignment
                        </span>
                        <span className="subHeaderTitle">
                          Service Executive Assignment and Category
                        </span>
                      </Col>
                    </Row>
                  </h2>

                  {showAssign && (
                    <div className="accordion-body">
                      {closureReturn?.seAssignment ? (
                        // ── Already assigned -- read-only display ──────────────
                        <Row className="align-items-center">
                          <Col
                            xs={12}
                            md={5}
                            className="commTopButtonRightLeftPadding"
                          >
                            <div className="border rounded p-3">
                              <p className="mb-1 fw-bold text-primary">
                                {closureReturn.seAssignment.name}
                              </p>
                              <p className="mb-0 text-muted">
                                Assigned by{" "}
                                {closureReturn.seAssignment.assignedBy}
                                &nbsp;·&nbsp;
                                {closureReturn.seAssignment.assignedAt?.slice(
                                  0,
                                  10,
                                )}
                              </p>
                              <p className="mb-0 text-muted">
                                Category:{" "}
                                <strong>{CLOSURE_ASSIGN_CATEGORY.name}</strong>
                              </p>
                            </div>
                          </Col>

                          {/* Dispatch can reassign */}
                          {isDispatch && (
                            <>
                              <Col
                                xs={12}
                                md={4}
                                className="commTopButtonRightLeftPadding"
                              >
                                <label className="form-label fw-medium">
                                  Reassign Service Executive
                                </label>
                                <select
                                  className="form-select form-select-sm"
                                  value={selectedSEUserId}
                                  onChange={(e) =>
                                    setSelectedSEUserId(e.target.value)
                                  }
                                >
                                  <option value="">Select…</option>
                                  {serviceExecutiveList.map((s) => (
                                    <option key={s.userId} value={s.userId}>
                                      {s.name}
                                    </option>
                                  ))}
                                </select>
                              </Col>
                              {selectedSEUserId && (
                                <Col
                                  xs={12}
                                  md={2}
                                  className="commTopButtonRightLeftPadding d-flex align-items-end"
                                >
                                  <button
                                    className="btnTable w-100"
                                    onClick={handleAssignSE}
                                    disabled={loading}
                                  >
                                    {loading ? "Saving…" : "Reassign"}
                                  </button>
                                </Col>
                              )}
                            </>
                          )}
                        </Row>
                      ) : isDispatch ? (
                        // ── Not yet assigned -- Dispatch Manager assigns ────────
                        <Row className="align-items-end">
                          <Col
                            xs={12}
                            md={5}
                            className="commTopButtonRightLeftPadding"
                          >
                            <label className="form-label fw-medium">
                              Select Service Executive
                            </label>
                            <select
                              className="form-select form-select-sm"
                              value={selectedSEUserId}
                              onChange={(e) =>
                                setSelectedSEUserId(e.target.value)
                              }
                            >
                              <option value="">
                                Select Service Executive…
                              </option>
                              {serviceExecutiveList.map((s) => (
                                <option key={s.userId} value={s.userId}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          </Col>
                          <Col
                            xs={12}
                            md={3}
                            className="commTopButtonRightLeftPadding"
                          >
                            <label className="form-label fw-medium">
                              Assignment Category
                            </label>
                            {/* Always "Receive Product" for closure flow -- shown as read-only */}
                            <input
                              className="form-control form-control-sm"
                              value={CLOSURE_ASSIGN_CATEGORY.name}
                              disabled
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={2}
                            className="commTopButtonRightLeftPadding d-flex align-items-end"
                          >
                            <button
                              className="btnTable w-100"
                              onClick={handleAssignSE}
                              disabled={loading || !selectedSEUserId}
                            >
                              {loading ? "Assigning…" : "Assign SE"}
                            </button>
                          </Col>
                        </Row>
                      ) : (
                        <p className="text-muted fst-italic mb-0">
                          Waiting for Dispatch Manager to assign a Service
                          Executive.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── 2. Return Items ─────────────────────────────────────────── */}
              <div className="accordion mb-3">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${showItems ? "bi-chevron-down" : "bi-chevron-right"}`}
                          onClick={() => setShowItems(!showItems)}
                        />
                      </Col>
                      <Col xs={1} className="text-center logoBtn">
                        <span className="iconLogo">RI</span>
                      </Col>
                      <Col xs={10}>
                        <span className="headerTitle">Return Items</span>
                        <span className="subHeaderTitle">
                          Collection and stock-in status per item
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  {showItems && (
                    <div className="accordion-body">
                      <DataTable
                        columns={itemColumns}
                        data={items}
                        noDataComponent={
                          <div className="p-3 text-center text-muted">
                            No items to collect
                          </div>
                        }
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* ── 4. Closure History ──────────────────────────────────────── */}
              <div className="accordion mb-3">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${showHistory ? "bi-chevron-down" : "bi-chevron-right"}`}
                          onClick={() => setShowHistory(!showHistory)}
                        />
                      </Col>
                      <Col xs={1} className="text-center logoBtn">
                        <span className="iconLogo">CH</span>
                      </Col>
                      <Col xs={10}>
                        <span className="headerTitle">Closure History</span>
                        <span className="subHeaderTitle">
                          All actions recorded in order
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  {showHistory && (
                    <div className="accordion-body p-0">
                      <DataTable
                        columns={historyColumns}
                        data={[...history].reverse()}
                        noDataComponent={
                          <div className="p-3 text-center text-muted">
                            No history yet
                          </div>
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Action Modal ───────────────────────────────────────────────────── */}
      <ClosureItemActionModal
        show={!!modal}
        onHide={() => setModal(null)}
        mode={modal?.mode}
        item={items[modal?.itemIndex]}
        onConfirm={onModalConfirm}
      />
    </div>
  );
};

export default ClosureReturnSection;
